import React from "react";
import { Stack, Alert } from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";
import SendIcon from "@mui/icons-material/Send";

import FormTextField from "../components/common/FormTextField";
import SubmitButton from "../components/buttons/SubmitButton";
import { useAppDispatch } from "../redux/store";
import { initiateConversation } from "../redux/actions/whatsappActions";
import { WhatsappTemplateRequestDto } from "../shared/dtos/whatsapp/WhatsappTemplateRequestDto";

// 1. Define the internal form state interface
interface WhatsappFormValues {
  recipients: string;
  templateId: string;
  paramsRaw: string;
}

// 2. Type the Yup schema
const whatsappSchema: Yup.ObjectSchema<WhatsappFormValues> = Yup.object().shape(
  {
    recipients: Yup.string().required("At least one recipient is required"),
    templateId: Yup.string().required("Template ID is required"),
    paramsRaw: Yup.string()
      .required("Variables JSON is required")
      .test("is-json", "Invalid JSON", (val) => {
        if (!val) return true;
        try {
          JSON.parse(val);
          return true;
        } catch {
          return false;
        }
      }),
  },
);

const InitiateWhatsappForm: React.FC<{ loading?: boolean }> = ({
  loading = false,
}) => {
  const dispatch = useAppDispatch();

  const initialValues: WhatsappFormValues = {
    recipients: "",
    templateId: "",
    paramsRaw: "{}",
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={whatsappSchema}
      onSubmit={(values) => {
        // Transform UI strings into DTO format
        const recipientsArray = values.recipients
          .split(",")
          .map((n) => n.trim())
          .filter((n) => n);

        const payload: WhatsappTemplateRequestDto = {
          recipients: recipientsArray,
          templateId: values.templateId,
          params: JSON.parse(values.paramsRaw) as Record<string, string>,
        };

        dispatch(initiateConversation(payload));
      }}
    >
      {({ dirty, isValid }: FormikProps<WhatsappFormValues>) => (
        <Form>
          <Stack spacing={3}>
            <Alert severity="info">
              New conversations require an approved Meta Template.
            </Alert>

            <FormTextField
              name="recipients"
              label="Recipients"
              placeholder="+254700..., +254711..."
              helperText="Comma separated numbers with country code."
            />

            <FormTextField
              name="templateId"
              label="Template ID"
              placeholder="hello_world_01"
            />

            <FormTextField
              name="paramsRaw"
              label="Variables (JSON)"
              multiline
              rows={3}
              helperText='e.g. {"1": "John"}'
            />

            <SubmitButton
              title="Initiate Message"
              loading={loading}
              disabled={!dirty || !isValid}
              startIcon={<SendIcon />}
              fullWidth
            />
          </Stack>
        </Form>
      )}
    </Formik>
  );
};

export default InitiateWhatsappForm;
